import {TabName} from "./Views/OrderEditor/OrderEditorTabs";
import React from "react";
import {IEmployee} from "../Interfaces/Admin/IEmployee";
import {
  ChatMessageSendEvent, 
  Message
} from "@progress/kendo-react-conversational-ui";
import {IApplication} from "../Interfaces/Application/IApplication";
import {
  getAllEmployeesInRoles,
  getAssignedEmployeeByRoleAndSequence,
  getRegionalManagersForSalesManager
} from "../utils/appUtils";
import {Constants} from "../utils/constants";
import {useDispatch, useSelector} from "react-redux";
import { AppState } from '../store/rootReducer';
import {addApplicationChat} from "../Data/ApplicationData";
import {ApplicationDispatch} from "../store/application/types";
import {IConfiguration} from "../Data/ConfigurationData";
import {
  AmsChat,
  ChatRecipient,
  convertChatsToKendoMessages
} from "@amsanalytics/react-shared-library";
import {Organization} from "@amsanalytics/react-shared-library/build/Models/Organization";
import {IEmployeeRole} from "../Interfaces/Admin/IEmployeeRole";

const ROLE_PREFIX = "role:";

interface ApplicationChatComponentProps {
  navigate: (tabName: TabName) => void;
  currentApp: IApplication;
  organizations: Organization[];
  configuration: IConfiguration;
}

export const ApplicationChatComponent: React.FC<ApplicationChatComponentProps> = (props) => {
  const applicationChats = useSelector((state: AppState) => state.applications.applicationChats);
  const activeUser: IEmployee = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const employees = useSelector((state: AppState) => state.applications.employees);
  const employeeRoles = useSelector((state: AppState) => state.applications.employeeRoles);
  const dispatch = useDispatch<ApplicationDispatch>();
  const salesManagerOnAppId = getAssignedEmployeeByRoleAndSequence(props.currentApp.applicationAssignments, Constants.roles.salesManagerId, 1)?.assignedToId;
  const regionalManagerIds = salesManagerOnAppId 
    ? getRegionalManagersForSalesManager(employees, props.organizations, salesManagerOnAppId)?.map(rm => rm.id) ?? []
    : [];
  const employeesToShowBasedOnRole = getAllEmployeesInRoles(props.configuration.applicationChatRecipientRoles, employees);
  const employeeIdsToShowBasedOnRole = employeesToShowBasedOnRole.map(e => e.id);
  const employeesAssignedToApplication = props.currentApp.applicationAssignments.map(aa => aa.assignedToId);
  const convertedChatMessages = convertChatsToKendoMessages(employees, applicationChats as Parameters<typeof convertChatsToKendoMessages>[1]);

  const roleRecipients: ChatRecipient[] = employeeRoles.map((role: IEmployeeRole) => ({
    id: `${ROLE_PREFIX}${role.id}`,
    firstName: "Role:",
    lastName: role.name
  }));

  const employeeRecipients: ChatRecipient[] = employees.filter(e =>
    e.id === activeUser.id
    || employeesAssignedToApplication.includes(e.id)
    || employeeIdsToShowBasedOnRole.includes(e.id)
    || regionalManagerIds.includes(e.id)
  );

  const availableRecipients: ChatRecipient[] = [...roleRecipients, ...employeeRecipients];

  const onAddNewMessage = (event: ChatMessageSendEvent, selectedRecipients: ChatRecipient[]) => {
    const roleTargets = selectedRecipients
      .filter(r => r.id.startsWith(ROLE_PREFIX))
      .map(r => ({ targetRoleId: r.id.replace(ROLE_PREFIX, '') }));
    const individualTargets = selectedRecipients
      .filter(r => !r.id.startsWith(ROLE_PREFIX))
      .map(r => ({ targetId: r.id }));

    const chatTargets = [...roleTargets, ...individualTargets];

    addApplicationChat({
      applicationId: props.currentApp!.id,
      createdBy: activeUser.id,
      createdDate: new Date(),
      message: event.message.text!,
      chatTargets
    }, dispatch);
  }

  const getRoleBadge = (chat: { createdBy: string }): string | undefined => {
    const sender = employees.find(e => e.id === chat.createdBy);
    if (!sender?.employeeEmployeeRoles?.length) return undefined;
    const primaryRole = employeeRoles.find(r => r.id === sender.employeeEmployeeRoles![0].employeeRoleId);
    return primaryRole?.name;
  };

  // Enhance messages with role badges
  const enhancedMessages: Message[] = convertedChatMessages.map((msg, idx) => {
    const chat = applicationChats[idx];
    const roleBadge = chat ? getRoleBadge(chat) : undefined;
    return {
      ...msg,
      text: roleBadge ? `[${roleBadge}] ${msg.text || ''}` : msg.text,
    };
  });

  return (
    <>
      {props.currentApp &&
        <div>
          <AmsChat
            availableRecipients={availableRecipients}
            messages={enhancedMessages}
            addNewMessage={(event: ChatMessageSendEvent, selectedRecipients: ChatRecipient[]) => onAddNewMessage(event, selectedRecipients)}
            user={{id: activeUser.id, name: `${activeUser.firstName} ${activeUser.lastName}`}}
          />
        </div>
      }
    </>
  );
}